/**
 * Google Analytics 4 (GA4) tracking utility.
 *
 * Tracking only fires in production when a measurement ID is configured.
 * In development, events are logged to the console instead of being sent so
 * the wiring can be verified without polluting GA4 data.
 */

import {
  applyConsent,
  DEFAULT_CONSENT,
  getStoredConsent,
} from "@/client/libs/consent";

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
const GOOGLE_ADS_ID = import.meta.env.VITE_GOOGLE_ADS_ID;

const isBrowser = typeof window !== "undefined";

/** Whether GA4 events should actually be sent to Google. */
export const isAnalyticsEnabled = Boolean(GA_MEASUREMENT_ID);

let initialized = false;

/**
 * Initializes GA4 and Google Ads gtag once. Safe to call multiple times —
 * subsequent calls are no-ops. Loads gtag.js lazily when a measurement ID is configured.
 */
export const initAnalytics = () => {
  if (!isBrowser || initialized || !isAnalyticsEnabled) return;
  initialized = true;

  window.dataLayer = window.dataLayer ?? [];

  if (typeof window.gtag !== "function") {
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer?.push(arguments);
    };

    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.append(script);

    window.gtag("js", new Date());
  }

  window.gtag("consent", "default", DEFAULT_CONSENT);

  const storedConsent = getStoredConsent();
  if (storedConsent) applyConsent(storedConsent);

  window.gtag("config", GA_MEASUREMENT_ID, { send_page_view: true });
  if (GOOGLE_ADS_ID) {
    window.gtag("config", GOOGLE_ADS_ID, { send_page_view: false });
  }
};

type EventParams = Record<string, unknown>;

const trackEvent = (name: string, params: EventParams = {}) => {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log(`[analytics] ${name}`, params);
  }

  if (!isAnalyticsEnabled || !isBrowser || typeof window.gtag !== "function") return;

  window.gtag("event", name, params);
};

/** GA4 standard recommended event — fire after a successful account creation. */
export const trackSignUp = (method = "email") => {
  trackEvent("sign_up", { method });
};

/** Custom event — fire when a resume is successfully created/finalized. */
export const trackResumeCreated = () => {
  trackEvent("resume_created");
};

/** Custom event — fire when a resume PDF is successfully downloaded. */
export const trackResumeDownload = () => {
  trackEvent("resume_downloaded");
};

/** Custom intent event — fire when the user clicks an upload entry point. */
export const trackUploadClicked = () => {
  trackEvent("upload_clicked");
};
